// canvas/bid-grid.js: B1 round 3 (Y1/Y2) bid-button grid placement.
//
// BiddingControls used to squeeze every round into layout.biddingZone,
// so a round-two grid on a short phone came out with 13-22px buttons.
// Now the layout lists free bands (layout.bidBands, best first) and this
// pure helper picks the first band that holds the round's rows at touch
// size: 40px on phones, 36px on desktop, up to 44/40px when there is
// room. Only when no band holds the rows does the font shrink (never
// below 11px). Shared by BiddingControls.jsx (render) and layout.js
// (which keeps the up-card clear of the largest grid).

export const BID_LINE_H = 18;   // dealer context line
export const BID_GAP = 4;       // grid gap, rows and columns
export const BID_PAD = 2;       // toolbar padding, each side
export const MIN_FONT = 11;

/**
 * Phones (portrait and short landscape) get the 40px touch minimum.
 *
 * @param {{portrait?: boolean, compact?: boolean, isMobile?: boolean}} layout
 */
export function isTouchLayout(layout) {
  return Boolean(layout.portrait || layout.compact || layout.isMobile);
}

/**
 * Width a button needs for the longest label ("Go Alone ♠") at `font`
 * px, plus a little side room. ~5.4em is above the rendered Nunito 600
 * width, so the text never touches the button edge.
 *
 * @param {number} font
 */
export function bidLabelWidth(font) {
  return Math.ceil(font * 5.4) + 6;
}

/**
 * Height of a grid with `rows` rows of `buttonHeight` buttons, dealer
 * line and padding included.
 *
 * @param {number} rows
 * @param {number} buttonHeight
 */
export function gridHeight(rows, buttonHeight) {
  return BID_LINE_H + 2 * BID_PAD + rows * (buttonHeight + BID_GAP);
}

/**
 * @typedef {{x:number, y:number, w:number, h:number,
 *   anchor:'top'|'center'|'bottom', fill:boolean, prefW?:number}} BidBand
 */

/**
 * Fit `count` buttons into one band at one font size, or null.
 *
 * @param {BidBand} band
 * @param {number} count
 * @param {number} font
 * @param {{minH:number, maxH:number}} sizes
 */
function fitBand(band, count, font, { minH, maxH }) {
  const labelW = bidLabelWidth(font);
  for (let rows = 1; rows <= count; rows += 1) {
    const columns = Math.ceil(count / rows);
    const needW = columns * labelW + (columns - 1) * BID_GAP + 2 * BID_PAD;
    if (needW > band.w) continue;
    const buttonHeight = Math.min(maxH,
      Math.floor((band.h - BID_LINE_H - 2 * BID_PAD) / rows) - BID_GAP);
    // More rows only make each row shorter.
    if (buttonHeight < minH) return null;
    const w = band.fill ? band.w : Math.min(band.w, Math.max(needW, band.prefW || 0));
    const h = gridHeight(rows, buttonHeight);
    const x = band.x + Math.floor((band.w - w) / 2);
    const y = band.anchor === 'bottom' ? band.y + band.h - h
      : band.anchor === 'top' ? band.y
        : band.y + Math.floor((band.h - h) / 2);
    return { zone: { x, y, w, h }, columns, rows, buttonHeight, fontSize: font };
  }
  return null;
}

/**
 * Pick the grid for `count` bid buttons.
 *
 * @param {any} layout  TableLayout (bidBands optional for old callers)
 * @param {number} count  3 in round one, 6 or 7 in round two
 * @returns {{zone:{x:number,y:number,w:number,h:number}, columns:number,
 *   rows:number, buttonHeight:number, fontSize:number, dealerFontSize:number,
 *   lineHeight:number, gap:number, cramped:boolean, bandIndex:number}}
 */
export function computeBidGrid(layout, count) {
  const touch = isTouchLayout(layout);
  const sizes = touch ? { minH: 40, maxH: 44 } : { minH: 36, maxH: 40 };
  // 14-12px is the normal range: within a band the fewest rows win,
  // then the larger font. 11px is the shrink step, tried only after
  // no band holds the rows at a normal size.
  const normalFonts = [14, 13, 12];
  /** @type {BidBand[]} */
  const bands = layout.bidBands && layout.bidBands.length
    ? layout.bidBands
    : [{ ...layout.biddingZone, anchor: 'top', fill: true }];
  const n = Math.max(1, count);
  const finish = (fit, cramped) => ({
    ...fit,
    dealerFontSize: touch ? Math.min(12, fit.fontSize) : 13,
    lineHeight: BID_LINE_H,
    gap: BID_GAP,
    cramped,
  });
  for (let bandIndex = 0; bandIndex < bands.length; bandIndex += 1) {
    let best = null;
    for (const font of normalFonts) {
      const fit = fitBand(bands[bandIndex], n, font, sizes);
      if (fit && (!best || fit.rows < best.rows)) best = fit;
    }
    if (best) return finish({ ...best, bandIndex }, false);
  }
  for (let bandIndex = 0; bandIndex < bands.length; bandIndex += 1) {
    const fit = fitBand(bands[bandIndex], n, MIN_FONT, sizes);
    if (fit) return finish({ ...fit, bandIndex }, false);
  }
  // Last resort (canvases below every supported size, e.g. 384x240):
  // keep the minimum button height in the tallest band even though the
  // grid then runs past it. Named so the caller can tell.
  const bandIndex = bands.reduce((best, b, i) => (b.h > bands[best].h ? i : best), 0);
  const band = bands[bandIndex];
  const columns = Math.max(1, Math.min(n,
    Math.floor((band.w - 2 * BID_PAD + BID_GAP) / (bidLabelWidth(MIN_FONT) + BID_GAP))));
  const rows = Math.ceil(n / columns);
  const h = gridHeight(rows, sizes.minH);
  return finish({
    zone: { x: band.x, y: Math.max(0, band.y + band.h - h), w: band.w, h },
    columns, rows, buttonHeight: sizes.minH, fontSize: MIN_FONT, bandIndex,
  }, true);
}
